import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import {
  NoCrmAccess, CustomerFields, customerPayload, blankCustomer,
  STATUS_LABELS, STATUS_BADGE, KIND_LABELS, formatDate,
} from './CrmCustomers';
import { groupLabel, groupBadge, money, pct } from './CrmInsights';

const CONSENT_LABELS = { unknown: 'Not asked', opted_in: 'Opted in', opted_out: 'Opted out' };
const CONSENT_BADGE = { unknown: 'badge-cancelled', opted_in: 'badge-approved', opted_out: 'badge-declined' };
const ACTIVITY_LABELS = { note: 'Note', call: 'Call', email: 'Email', meeting: 'Meeting', quote: 'Quote', order: 'Order', task: 'Follow-up' };

const blankContact = {
  first_name: '', last_name: '', job_title: '', email: '', phone: '', mobile: '', is_primary: false,
  email_consent: 'unknown', email_consent_source: '', sms_consent: 'unknown', sms_consent_source: '',
};

const contactName = (k) => [k.first_name, k.last_name].filter(Boolean).join(' ') || k.email || 'Unnamed';

const CrmCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [c, setC] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noAccess, setNoAccess] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blankCustomer);
  const [contact, setContact] = useState(blankContact);
  const [contactId, setContactId] = useState(null);
  const [activity, setActivity] = useState({ kind: 'note', body: '', due_date: '', contact_id: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const [ins, setIns] = useState(null);

  const denied = (err) => {
    if (err.response?.status !== 403 || !/access to the CRM/.test(err.response?.data?.error || '')) return false;
    setNoAccess(true);
    return true;
  };
  const fail = (err, fallback) => { if (!denied(err)) setError(err.response?.data?.error || fallback); };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/customers/${id}`);
      setC(res.data);
    } catch (err) {
      if (err.response?.status === 404) setError('Customer not found');
      else fail(err, 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/crm/staff').then(r => setStaff(r.data)).catch(() => {}); }, []);
  // Sales, margin and score: only for people with Customer insights switched on.
  useEffect(() => {
    if (!user?.can_insights) return;
    api.get(`/api/insights/customers/${id}`).then(r => setIns(r.data)).catch(() => setIns(null));
  }, [id, user?.can_insights]);

  const openEdit = () => {
    const f = {};
    for (const k of Object.keys(blankCustomer)) f[k] = c[k] === null || c[k] === undefined ? '' : c[k];
    f.tags = (c.tags || []).join(', ');
    f.owner_id = c.owner_id ? String(c.owner_id) : '';
    setForm(f);
    setError('');
    setModal('edit');
  };

  const saveCustomer = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/api/crm/customers/${id}`, customerPayload(form));
      setModal(null);
      load();
    } catch (err) { fail(err, 'Failed to save'); } finally { setSaving(false); }
  };

  const openContact = (k) => {
    if (k) {
      const f = {};
      for (const key of Object.keys(blankContact)) f[key] = k[key] === null || k[key] === undefined ? (typeof blankContact[key] === 'boolean' ? false : '') : k[key];
      // Sources are recorded when consent changes; start blank so an unchanged value keeps the saved one.
      f.email_consent_source = ''; f.sms_consent_source = '';
      setContact(f); setContactId(k.id);
    } else {
      setContact({ ...blankContact, is_primary: !(c.contacts || []).length }); setContactId(null);
    }
    setError('');
    setModal('contact');
  };

  const saveContact = async () => {
    setSaving(true); setError('');
    try {
      if (contactId) await api.put(`/api/crm/contacts/${contactId}`, contact);
      else await api.post(`/api/crm/customers/${id}/contacts`, contact);
      setModal(null);
      load();
    } catch (err) { fail(err, 'Failed to save contact'); } finally { setSaving(false); }
  };

  const deleteContact = async () => {
    if (!window.confirm('Remove this contact permanently?')) return;
    try {
      await api.delete(`/api/crm/contacts/${contactId}`);
      setModal(null);
      load();
    } catch (err) { fail(err, 'Failed to remove contact'); }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!activity.body.trim()) return setError('Please write something');
    if (activity.kind === 'task' && !activity.due_date) return setError('A follow-up needs a due date');
    setSaving(true); setError('');
    try {
      await api.post(`/api/crm/customers/${id}/activities`, {
        ...activity,
        contact_id: activity.contact_id ? Number(activity.contact_id) : null,
        assigned_to: activity.assigned_to ? Number(activity.assigned_to) : null,
      });
      setActivity({ kind: activity.kind, body: '', due_date: '', contact_id: '', assigned_to: '' });
      load();
    } catch (err) { fail(err, 'Failed to save'); } finally { setSaving(false); }
  };

  const toggleDone = async (a) => {
    try { await api.patch(`/api/crm/activities/${a.id}`, { done: !a.done }); load(); } catch (err) { fail(err, 'Failed to update'); }
  };
  const removeActivity = async (a) => {
    if (!window.confirm('Remove this entry?')) return;
    try { await api.delete(`/api/crm/activities/${a.id}`); load(); } catch (err) { fail(err, 'Failed to remove'); }
  };

  const setArchived = async (archived) => {
    try { await api.post(`/api/crm/customers/${id}/archive`, { archived }); load(); } catch (err) { fail(err, 'Failed to update'); }
  };
  const deleteCustomer = async () => {
    if (!window.confirm(`Delete ${c.name} and all its contacts and history permanently? This cannot be undone. Use it for a data erasure request; otherwise archive.`)) return;
    try { await api.delete(`/api/crm/customers/${id}`); navigate('/crm'); } catch (err) { fail(err, 'Failed to delete'); }
  };

  if (noAccess) return <NoCrmAccess />;
  if (loading) return <div className="loader">Loading...</div>;
  if (!c) return <div><div className="alert alert-error">{error || 'Customer not found'}</div><Link to="/crm">Back to customers</Link></div>;

  const field = (label, value) => value ? (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', color: 'var(--grey-mid)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '14px', wordBreak: 'break-word' }}>{value}</div>
    </div>
  ) : null;
  const address = [c.address_line1, c.address_line2, c.town, c.county, c.postcode].filter(Boolean).join(', ');
  const money = (v) => (v === null || v === undefined ? null : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const has = (v) => v !== null && v !== undefined && v !== '';
  // Trade account details come from the ERP import; the panel only shows when there are some.
  const isTrade = [c.vat_number, c.company_number, c.credit_terms_days, c.credit_limit, c.depot, c.xero_id, c.erp_synced_at].some(has);
  // Only a real invoice address is worth showing; a postcode on its own is just the registered one again.
  const invoiceAddress = c.invoice_address ? [c.invoice_address, c.invoice_postcode].filter(Boolean).join(', ') : null;
  const formChange = (e) => { const { name, value } = e.target; setForm(p => ({ ...p, [name]: value })); setError(''); };
  const contactChange = (e) => { const { name, value, type, checked } = e.target; setContact(p => ({ ...p, [name]: type === 'checkbox' ? checked : value })); setError(''); };

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/crm" style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>&larr; Customers</Link>
          <h1 className="page-title">{c.name}</h1>
          <p className="page-subtitle">
            {KIND_LABELS[c.kind]} &middot; <span className={`badge ${STATUS_BADGE[c.status]}`}>{STATUS_LABELS[c.status]}</span>
            {c.archived && <> &middot; <span className="badge badge-declined">Archived</span></>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={openEdit}>Edit</button>
          {c.archived
            ? <button className="btn btn-outline" onClick={() => setArchived(false)}>Restore</button>
            : <button className="btn btn-outline" onClick={() => setArchived(true)}>Archive</button>}
          {isAdmin && <button className="btn btn-danger" onClick={deleteCustomer}>Delete</button>}
        </div>
      </div>

      {error && !modal && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', alignItems: 'start' }}>
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-header"><h3 className="card-title">Details</h3></div>
            <div className="card-body">
              {field('Account number', c.account_number)}
              {field('Email', c.email && <a href={`mailto:${c.email}`}>{c.email}</a>)}
              {field('Phone', c.phone && <a href={`tel:${c.phone}`}>{c.phone}</a>)}
              {field('Website', c.website)}
              {field('Address', address)}
              {field('Account owner', c.owner_name)}
              {field('Came from', c.source)}
              {field('Tags', (c.tags || []).join(', '))}
              {field('Notes', c.notes && <span style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</span>)}
              <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>Added {formatDate(c.created_at)}{c.created_by_name ? ` by ${c.created_by_name}` : ''}</div>
            </div>
          </div>

          {isTrade && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="card-header"><h3 className="card-title">Trade account</h3></div>
              <div className="card-body">
                {field('Depot', c.depot)}
                {field('Credit terms', has(c.credit_terms_days) ? (Number(c.credit_terms_days) === 0 ? 'None (pay on order)' : `${c.credit_terms_days} days`) : null)}
                {field('Credit limit', money(c.credit_limit))}
                {field('Estimated monthly spend', money(c.estimated_monthly_spend))}
                {field('VAT number', c.vat_number)}
                {field('Company number', c.company_number)}
                {field('Invoice address', invoiceAddress)}
                {field('Xero ID', c.xero_id)}
                {field('Opened in the ERP', c.erp_created_at && formatDate(c.erp_created_at))}
                {field('Approved', c.erp_approved_at && formatDate(c.erp_approved_at))}
                {field('Stopped', c.erp_stopped_at && formatDate(c.erp_stopped_at))}
                {c.erp_synced_at && <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>From the ERP, last updated {formatDate(c.erp_synced_at)}. Changes here are overwritten by the next ERP import; change them in the ERP.</div>}
              </div>
            </div>
          )}

          {ins?.insight && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="card-header">
                <h3 className="card-title">Insights</h3>
                <span className={`badge ${groupBadge(ins.insight.grp)}`}>{groupLabel(ins.insight.grp)}</span>
              </div>
              <div className="card-body">
                {(ins.insight.ai_note || ins.insight.rule_note) && <p style={{ marginTop: 0, fontSize: '14px' }}>{ins.insight.ai_note || ins.insight.rule_note}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '4px 16px' }}>
                  {field('Score', ins.insight.is_channel ? 'Sales channel, not scored' : ins.insight.score === null ? null : `${ins.insight.score} of 100`)}
                  {field('Last order', ins.insight.last_order_on && `${formatDate(ins.insight.last_order_on)} (${ins.insight.days_since === 1 ? '1 day' : `${ins.insight.days_since} days`} ago)`)}
                  {field('Usually orders every', ins.insight.usual_gap_days && `${Math.round(ins.insight.usual_gap_days)} days`)}
                  {field('Last 8 weeks', ins.insight.change_pct !== null ? `${money(ins.insight.recent_spend)} (${pct(ins.insight.change_pct)})` : money(ins.insight.recent_spend))}
                  {field('12 month sales', money(ins.insight.sales_12m))}
                  {field('12 month margin', ins.insight.sales_12m > 0 ? `${money(ins.insight.margin_12m)} (${Math.round((ins.insight.margin_12m / ins.insight.sales_12m) * 100)}%)` : null)}
                  {field('Delivery cost', `${money(ins.insight.delivery_cost_12m)}, ${ins.insight.deliveries_12m} drops`)}
                  {field('Net contribution', money(ins.insight.net_12m))}
                </div>
                {ins.months.length > 0 && (
                  <div className="table-wrap" style={{ marginTop: '8px' }}>
                    <table>
                      <thead><tr><th>Month</th><th>Sales</th><th>Margin</th><th>Orders</th></tr></thead>
                      <tbody>{ins.months.map(m => <tr key={m.month}><td>{m.month.slice(5)}/{m.month.slice(0, 4)}</td><td>{money(m.sales)}</td><td>{money(m.margin)}</td><td>{m.orders}</td></tr>)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Contacts ({c.contacts.length})</h3>
              <button className="btn btn-outline btn-sm" onClick={() => openContact(null)}>+ Add contact</button>
            </div>
            {c.contacts.length === 0 ? (
              <div className="empty-state"><p>No contacts yet</p></div>
            ) : c.contacts.map(k => (
              <div key={k.id} className="card-body" style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => openContact(k)}>
                <div style={{ fontWeight: 600 }}>{contactName(k)}{k.is_primary && <span style={{ fontSize: '11px', color: 'var(--grey-mid)', fontWeight: 400 }}> &middot; main contact</span>}</div>
                {k.job_title && <div style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>{k.job_title}</div>}
                <div style={{ fontSize: '13px' }}>{[k.email, k.mobile, k.phone].filter(Boolean).join(' · ')}</div>
                <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap', fontSize: '12px' }}>
                  Email <span className={`badge ${CONSENT_BADGE[k.email_consent]}`}>{CONSENT_LABELS[k.email_consent]}</span>
                  SMS <span className={`badge ${CONSENT_BADGE[k.sms_consent]}`}>{CONSENT_LABELS[k.sms_consent]}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="card-title">Timeline</h3></div>
          <div className="card-body">
            <form onSubmit={addActivity}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0 12px' }}>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-control" value={activity.kind} onChange={e => setActivity(a => ({ ...a, kind: e.target.value }))}>
                    {Object.entries(ACTIVITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                {c.contacts.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">With</label>
                    <select className="form-control" value={activity.contact_id} onChange={e => setActivity(a => ({ ...a, contact_id: e.target.value }))}>
                      <option value="">Nobody in particular</option>
                      {c.contacts.map(k => <option key={k.id} value={k.id}>{contactName(k)}</option>)}
                    </select>
                  </div>
                )}
                {activity.kind === 'task' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Due</label>
                      <input type="date" className="form-control" value={activity.due_date} onChange={e => setActivity(a => ({ ...a, due_date: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">For</label>
                      <select className="form-control" value={activity.assigned_to} onChange={e => setActivity(a => ({ ...a, assigned_to: e.target.value }))}>
                        <option value="">Me</option>
                        {staff.filter(s => s.id !== user?.id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="form-group">
                <textarea className="form-control" rows={3} value={activity.body} onChange={e => setActivity(a => ({ ...a, body: e.target.value }))}
                  placeholder={activity.kind === 'task' ? 'What needs doing' : 'What happened'} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add'}</button>
            </form>
          </div>
          {c.activities.length === 0 ? (
            <div className="empty-state"><p>Nothing logged yet</p></div>
          ) : c.activities.map(a => (
            <div key={a.id} className="card-body" style={{ borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>
                  <strong style={{ color: 'var(--navy)' }}>{ACTIVITY_LABELS[a.kind]}</strong>
                  {a.contact_name && <> with {a.contact_name}</>} &middot; {a.created_by_name || 'Unknown'} &middot; {formatDate(a.created_at)}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {a.kind === 'task' && (
                    <button className="btn btn-outline btn-sm" onClick={() => toggleDone(a)}>{a.done ? 'Reopen' : 'Done'}</button>
                  )}
                  {(a.created_by === user?.id || isAdmin) && (
                    <button className="btn btn-outline btn-sm" onClick={() => removeActivity(a)}>Remove</button>
                  )}
                </div>
              </div>
              {a.kind === 'task' && (
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  {a.done
                    ? <span className="badge badge-approved">Done {formatDate(a.done_at)}</span>
                    : <span className="badge badge-pending">Due {formatDate(a.due_date)}</span>}
                  {a.assigned_to_name && <span style={{ color: 'var(--grey-mid)' }}> for {a.assigned_to_name}</span>}
                </div>
              )}
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '6px', fontSize: '14px', textDecoration: a.done ? 'line-through' : 'none' }}>{a.body}</div>
            </div>
          ))}
        </div>
      </div>

      {modal === 'edit' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" style={{ maxWidth: '640px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit customer</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <CustomerFields form={form} onChange={formChange} staff={staff} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCustomer} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'contact' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{contactId ? 'Edit contact' : 'Add contact'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
                <div className="form-group"><label className="form-label">First name</label><input name="first_name" className="form-control" value={contact.first_name} onChange={contactChange} autoComplete="off" /></div>
                <div className="form-group"><label className="form-label">Last name</label><input name="last_name" className="form-control" value={contact.last_name} onChange={contactChange} autoComplete="off" /></div>
                <div className="form-group"><label className="form-label">Job title</label><input name="job_title" className="form-control" value={contact.job_title} onChange={contactChange} autoComplete="off" /></div>
                <div className="form-group"><label className="form-label">Email</label><input name="email" type="email" className="form-control" value={contact.email} onChange={contactChange} autoComplete="off" /></div>
                <div className="form-group"><label className="form-label">Mobile</label><input name="mobile" type="tel" className="form-control" value={contact.mobile} onChange={contactChange} autoComplete="off" /></div>
                <div className="form-group"><label className="form-label">Other phone</label><input name="phone" type="tel" className="form-control" value={contact.phone} onChange={contactChange} autoComplete="off" /></div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '16px' }}>
                <input type="checkbox" name="is_primary" checked={contact.is_primary} onChange={contactChange} style={{ accentColor: 'var(--red)', width: '16px', height: '16px' }} />
                <span className="form-label" style={{ margin: 0 }}>Main contact</span>
              </label>

              <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Marketing consent</h4>
              <p style={{ fontSize: '12px', color: 'var(--grey-mid)', marginTop: 0 }}>
                Only mark someone opted in when they have actually said yes, and say how. SMS needs its own yes, separate from email. The date is recorded when you save.
              </p>
              {['email', 'sms'].map(kind => {
                const saved = contactId ? c.contacts.find(k => k.id === contactId) : null;
                return (
                  <div key={kind} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
                    <div className="form-group">
                      <label className="form-label">{kind === 'email' ? 'Email' : 'SMS'}</label>
                      <select name={`${kind}_consent`} className="form-control" value={contact[`${kind}_consent`]} onChange={contactChange}>
                        {Object.entries(CONSENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      {saved && saved[`${kind}_consent_at`] && (
                        <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>
                          {CONSENT_LABELS[saved[`${kind}_consent`]]} {formatDate(saved[`${kind}_consent_at`])}: {saved[`${kind}_consent_source`]}
                        </p>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">How they said so</label>
                      <input name={`${kind}_consent_source`} className="form-control" value={contact[`${kind}_consent_source`]} onChange={contactChange}
                        placeholder="e.g. ticked the box at the trade counter" autoComplete="off" />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              {contactId && isAdmin && <button className="btn btn-danger" onClick={deleteContact} style={{ marginRight: 'auto' }}>Remove</button>}
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveContact} disabled={saving}>{saving ? 'Saving...' : 'Save contact'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmCustomer;
